<script setup lang="ts">
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import { CompanyEditTab } from "@/modules/companies/interfaces/companyEditTab";
import CreateRoleModal from "@/modules/roles/components/CreateRoleModal.vue";
import RoleEntry from "@/modules/roles/components/RoleEntry.vue";
import RoleList from "@/modules/roles/components/RoleList.vue";
import useRole from "@/modules/roles/composables/useRole";

import useRoles, { Role } from "@/modules/roles/composables/useRoles";
import { useQueryClient } from "@tanstack/vue-query";
import { provide, ref } from "vue";
import { useRoute } from "vue-router";

interface Props extends CompanyEditTab {}

const props = defineProps<Props>();
const queryClient = useQueryClient();

const route = useRoute();

const { data, isLoading, refetch } = useRoles({
  companyId: Number(route.params.id),
  immediate: true,
});

const { deleteRoleMutationAsync } = useRole({
  id: 0,
  immediate: false,
});

const invalidateQueryData = async () => {
  await queryClient.invalidateQueries(["rolesCompany"]);
  await refetch();
};

const selectedRole = ref<Role>();
const containerRef = ref(null);

const action = ref(false);

const actionModal = () => {
  return (action.value = !action.value);
};

provide("invalidateQueryData", { invalidateQueryData });

const handleDelete = async (id: string | number) => {
  await deleteRoleMutationAsync(id);

  await refetch();
};
</script>

<template>
  <ShadowBox class="bg-white">
    <div class="flex justify-between items-center mb-5">
      <div>
        <h1 class="font-medium text-2xl">Roles de la empresa</h1>
      </div>

      <HasPermission
        name-permission="create-roles"
        :entity-type="'App\\Models\\Company'"
      >
        <StyledButton
          class="rounded-md bg-blue-500 py-2 px-5 text-white hover:bg-blue-700 transition-colors"
          to="/roles/create"
          @click="actionModal"
        >
          <div class="flex items-center justify-around gap-2">
            <span class="material-symbols-outlined"> policy </span>
            Agregar rol
          </div>
        </StyledButton>
      </HasPermission>

      <CreateRoleModal
        :visible="action"
        :company-selected="props.company"
        @action-modal="actionModal"
      />
    </div>

    <RoleList :roles="data || []" :selected-role="selectedRole">
      <template #default="{ role }">
        <RoleEntry
          :role="role"
          show-edit="companies.edit.roles.internal"
          @delete-role="handleDelete"
        />
      </template>
    </RoleList>
  </ShadowBox>

  <LoadingModal :when="isLoading" />
</template>
